import { ChangeEvent, useEffect, useState } from 'react';
import styles from './ListComponents.module.css'
import { ComponentFile, useComponentContext } from '../../context/ComponentBriefingContextProvider';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'

import { ModalImage } from '../ModalImage';
import { ComponentForm } from '../ComponentForm';
import { set } from 'date-fns';

interface ComponentPorps {
    idClient: number
    idBriefing: number,
    idAttendance?: number
}

export interface Component {
    id: number
    briefing_id: number
    posicao: number
    conteudo?: string
    tipoComponente: 'texto' | 'titulo' | 'img' | 'pdf' | 'audio'
    componentImgs?: ComponentFile[]
}

export function ListComponents({ idClient, idBriefing, idAttendance }: ComponentPorps) {
    const [content, setContent] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [text, setText] = useState<string>('');

    const [files, setFiles] = useState<File[]>([]);
    const [fileURLs, setFileURLs] = useState<string[]>([]);

    const [images, setImages] = useState<File[]>([])
    const [imageURLs, setImageURLs] = useState<string[]>([])

    const [audios, setAudios] = useState<File[]>([])
    const [audioURLs, setAudioURLs] = useState<string[]>([])

    const [idComponent, setIdComponent] = useState<number>(0);
    const [isIdEditing, setIdIdEditing] = useState<number>();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const { updateComponent, deleteFile, deleteComponent, fetchComponents, updatePosition, listFile, components } = useComponentContext();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<ComponentFile[]>([]);

    useEffect(() => {
        getComponents();
    }, [idComponent]);

    useEffect(() => {
        fetchComponents(idBriefing);
    }, [idBriefing]);

    useEffect(() => {
        const currentComponent = components.find(component => component.id === idComponent);
        if (currentComponent) {
            if (currentComponent.tipoComponente === 'titulo') {
                setTitle(currentComponent.conteudo || '');
            } else if (currentComponent.tipoComponente === 'texto') {
                setText(currentComponent.conteudo || '');
            }
        }
    }, [idComponent, components]);


    useEffect(() => {
        const newImgURLs = images.map(file => URL.createObjectURL(file))
        setImageURLs(newImgURLs)

        const newFileURLs = files.map(file => URL.createObjectURL(file))
        setFileURLs(newFileURLs)

        const newAudioURLs = audios.map(file => URL.createObjectURL(file))
        setAudioURLs(newAudioURLs)


        return () => {
            newImgURLs.forEach(URL.revokeObjectURL)
            newFileURLs.forEach(URL.revokeObjectURL)
            newAudioURLs.forEach(URL.revokeObjectURL)
        }
    }, [images, files, audios])

    function getComponents() {
        fetchComponents(idBriefing);
    }

    function reset() {
        setFiles([]);
        setFileURLs([]);
        setImages([]);
        setImageURLs([]);
        setContent('');
        setAudioURLs([]);
        setAudios([]);
    }

    function handleOpenFile(idComponent: number) {
        const files = listFile.filter(file => file.componente_id === idComponent);
        setSelectedFiles(files);
        setModalOpen(true);
    }

    function handleCloseModal() {
        setModalOpen(false);
    }

    function handleChangeTitle(event: ChangeEvent<HTMLInputElement>) {
        const data = event.target.value;
        setTitle(data);
        setContent(data);
    }

    function handleChangeText(event: string) {
        setText(event);
    }

    function handleChangeEditComponent(idComponent: number) {
        setIdIdEditing(idComponent);
        setIsEditing(!isEditing);
        setIdComponent(idComponent);
    }

    function handleChangeFile(event: ChangeEvent<HTMLInputElement>) {
        const newFiles = event.target.files

        if (newFiles) {
            const newImages: File[] = []
            const otherFiles: File[] = []
            const arquivoAudio: File[] = []

            for (let i = 0; i < newFiles.length; i++) {
                const file = newFiles[i]
                if (file.type.match('image.*')) {
                    newImages.push(file)
                } else if (file.type.match('audio.*') || file.type.match('.ogg') || file.type.match('.mp3') || file.type.match('.wav')) {
                    console.log('Audio', file)
                    arquivoAudio.push(file)
                } else if (file.type === 'application/pdf' || file.type.match('application/msword') || file.type.match('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
                    otherFiles.push(file)
                }
            }

            setImages(prevImages => [...prevImages, ...newImages])
            setFiles(prevFiles => [...prevFiles, ...otherFiles])
            setAudios(prevAudios => [...prevAudios, ...arquivoAudio])
        }
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const textoAtualizado = text;

        if (files.length > 0) {
            updateComponent(idClient, idBriefing, idComponent, idAttendance, content, files);
        } else if (images.length > 0) {
            updateComponent(idClient, idBriefing, idComponent, idAttendance, content, images);
        } else if (audios.length > 0) {
            updateComponent(idClient, idBriefing, idComponent, idAttendance, content, audios);
        }
        else {
            updateComponent(0, idBriefing, idComponent, undefined, textoAtualizado);
        }

        getComponents();
        setIsEditing(false);
        reset();
    }

    async function handleDeleteComponent(idComponent: number) {
        await deleteComponent(idBriefing, idComponent);
        getComponents();
    }

    function handleDeleteFile(idFile: number) {
        deleteFile(idBriefing, idFile);
        getComponents();
    }


    function onDragEnd(result: DropResult) {
        const { source, destination } = result
        if (!destination) {
            return
        }
        if (source.index !== destination.index) {
            const newComponents = Array.from(components)
            const [removed] = newComponents.splice(source.index, 1)
            newComponents.splice(destination.index, 0, removed)

            // Enviar as novas posições para o api
            const updates = newComponents.map((item, index) => ({ id: item.id, position: index + 1 }))

            updatePosition(updates, idBriefing)
        }
    }

    return (
        <>
            <ModalImage open={modalOpen} images={selectedFiles} onClose={handleCloseModal} />
            <DragDropContext
                onDragEnd={onDragEnd}
            >
                <Droppable
                    droppableId='ROOT'
                    type='group'
                >
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {components.map((item, index) => (
                                <Draggable
                                    draggableId={item.id.toString()}
                                    key={item.id}
                                    index={index}
                                >
                                    {(provided) => (
                                        <div
                                            className={styles.container_component}
                                            ref={provided.innerRef}
                                            {...provided.dragHandleProps}
                                            {...provided.draggableProps}
                                        >
                                            <DragIndicatorIcon
                                                className={styles.drag_indicator}
                                            />
                                            <ComponentForm
                                                key={index}
                                                item={item}
                                                isEditing={isEditing}
                                                isIdEditing={isIdEditing}
                                                titulo={title}
                                                texto={text}
                                                fileURLs={fileURLs}
                                                files={files}
                                                imageURLs={imageURLs}
                                                audioURLs={audioURLs}
                                                audios={audios}
                                                handleSubmit={handleSubmit}
                                                handleDeleteComponent={handleDeleteComponent}
                                                handleChangeTitle={handleChangeTitle}
                                                handleChangeText={handleChangeText}
                                                handleChangeEditComponent={handleChangeEditComponent}
                                                handleChangeFile={handleChangeFile}
                                                handleDeleteFile={handleDeleteFile}
                                                handleOpenFile={handleOpenFile}
                                                listFile={listFile}
                                                setText={setText}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
}